import React, { useMemo } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  LinearProgress,
  Stack,
  Typography,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import { formatDistanceToNow } from "date-fns";

import { useApiClient } from "../../../api";
import PaginatedBucketList from "../../../shared-components/PaginatedBucketList";
import { enqueueSnackbar } from "notistack";

const BULK_FILE_UPLOAD_BUCKETS = [
  {
    value: "PENDING",
    label: "Pending",
    countKey: "pending",
  },
  {
    value: "IN_PROGRESS",
    label: "Processing",
    countKey: "inProgress",
  },
  {
    value: "FAILED",
    label: "Failed",
    countKey: "failed",
  },
  {
    value: "COMPLETED",
    label: "Completed",
    countKey: "completed",
  },
];

const getLatestVisibleLog = (bulkUpload) =>
  bulkUpload.job?.logs?.find((log) => !log.hideFromUser);

const getStatusChip = (status) => {
  switch (status) {
    case "PENDING":
      return <Chip label="Pending" size="small" />;

    case "IN_PROGRESS":
      return <Chip label="Processing" color="primary" size="small" />;

    case "FAILED":
      return (
        <Chip icon={<ErrorIcon />} label="Failed" color="error" size="small" />
      );

    case "COMPLETED":
      return (
        <Chip
          icon={<CheckCircleIcon />}
          label="Completed"
          color="success"
          size="small"
        />
      );

    default:
      return <Chip label={status || "Unknown"} size="small" />;
  }
};

const getUploadScope = (bulkUpload) => {
  if (bulkUpload.assignment?.name) {
    return bulkUpload.assignment.name;
  }

  if (bulkUpload.assignmentGroup?.name) {
    return bulkUpload.assignmentGroup.name;
  }

  return null;
};

const BulkFileUploadCard = ({ bulkUpload, onRetry }) => {
  const status = bulkUpload.processingStatus;

  const progress = bulkUpload.job?.progress || 0;

  const latestLog = getLatestVisibleLog(bulkUpload);

  const scope = getUploadScope(bulkUpload);

  return (
    <Card variant="outlined">
      <CardContent>
        <Stack spacing={1.5}>
          <Stack
            direction={{
              xs: "column",
              sm: "row",
            }}
            spacing={2}
            justifyContent="space-between"
            alignItems={{
              xs: "stretch",
              sm: "flex-start",
            }}
          >
            <Box sx={{ flex: 1 }}>
              <Typography variant="h6">
                {bulkUpload.originalFilename}
              </Typography>

              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                flexWrap="wrap"
                useFlexGap
                sx={{ mt: 1 }}
              >
                {getStatusChip(status)}

                {bulkUpload.uploadedAt && (
                  <Typography variant="caption" color="text.secondary">
                    Uploaded{" "}
                    {formatDistanceToNow(new Date(bulkUpload.uploadedAt), {
                      addSuffix: true,
                    })}
                  </Typography>
                )}
              </Stack>
            </Box>

            {status === "FAILED" && (
              <Button variant="outlined" color="error" onClick={onRetry}>
                Retry
              </Button>
            )}
          </Stack>

          {(scope || bulkUpload.uploadedBy?.email) && (
            <Stack spacing={0.25}>
              {scope && (
                <Typography variant="body2" color="text.secondary">
                  {bulkUpload.assignment ? "Assignment" : "Assignment Group"}:{" "}
                  {scope}
                </Typography>
              )}

              {bulkUpload.uploadedBy?.email && (
                <Typography variant="body2" color="text.secondary">
                  Uploaded by {bulkUpload.uploadedBy.email}
                </Typography>
              )}
            </Stack>
          )}

          {status === "PENDING" && (
            <Typography variant="body2" color="text.secondary">
              {latestLog?.message || "Waiting to start..."}
            </Typography>
          )}

          {status === "IN_PROGRESS" && (
            <Box>
              <LinearProgress variant="determinate" value={progress} />

              <Typography
                variant="caption"
                color="text.secondary"
                sx={{
                  mt: 0.5,
                  display: "block",
                }}
              >
                {progress}% complete
                {latestLog?.message && ` - ${latestLog.message}`}
              </Typography>
            </Box>
          )}

          {status === "FAILED" && (
            <Typography variant="body2" color="error">
              {latestLog?.message || "Processing failed."}
            </Typography>
          )}

          {status === "COMPLETED" && bulkUpload.processedAt && (
            <Typography variant="body2" color="text.secondary">
              Completed{" "}
              {formatDistanceToNow(new Date(bulkUpload.processedAt), {
                addSuffix: true,
              })}
            </Typography>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
};

const BulkUploadSubmissionJobs = ({
  assignmentId = null,
  assignmentGroupId = null,
  refreshKey = 0,
}) => {
  const ApiClient = useApiClient();

  const baseFilters = useMemo(
    () => ({
      assignment: assignmentId,
      assignmentGroup: assignmentGroupId,
    }),
    [assignmentId, assignmentGroupId],
  );

  const handleRetry = async (bulkUpload, refetch, refreshCounts) => {
    try {
      const response = await ApiClient.assignment.retryBulkFileUpload({
        bulkFileUpload: bulkUpload.id,
      });

      if (response?.success) {
        enqueueSnackbar("Retrying bulk upload processing...", {
          variant: "success",
        });

        await refetch();
        await refreshCounts();
      } else {
        enqueueSnackbar(response?.message || "Failed to retry bulk upload.", {
          variant: "error",
        });
      }
    } catch (error) {
      console.error("Error retrying bulk upload:", error);

      enqueueSnackbar(`Error retrying bulk upload: ${error.message}`, {
        variant: "error",
      });
    }
  };

  return (
    <PaginatedBucketList
      title="Bulk Uploads"
      buckets={BULK_FILE_UPLOAD_BUCKETS}
      defaultBucket="IN_PROGRESS"
      baseFilters={baseFilters}
      searchFilterName="searchString"
      searchPlaceholder="Search filename..."
      pageSize={5}
      enabled={Boolean(assignmentId || assignmentGroupId)}
      refreshKey={refreshKey}
      fetchItems={({ first, after, ...filters }) =>
        ApiClient.assignment.getBulkFileUploads({
          first,
          after,
          ...filters,
        })
      }
      fetchCounts={(filters) =>
        ApiClient.assignment.getBulkFileUploadCounts(filters)
      }
      emptyMessage={(bucket, searching) => {
        if (searching) {
          return "No bulk uploads match your search.";
        }

        switch (bucket) {
          case "PENDING":
            return "No bulk uploads are waiting to start.";

          case "IN_PROGRESS":
            return "No bulk uploads are currently processing.";

          case "FAILED":
            return "No failed bulk uploads.";

          case "COMPLETED":
            return "No completed bulk uploads.";

          default:
            return "No bulk uploads.";
        }
      }}
      renderItem={(bulkUpload, bucket, { refetch, refreshCounts }) => (
        <BulkFileUploadCard
          bulkUpload={bulkUpload}
          onRetry={
            bucket === "FAILED"
              ? async () => {
                  await handleRetry(bulkUpload, refetch, refreshCounts);
                }
              : undefined
          }
        />
      )}
    />
  );
};

export default BulkUploadSubmissionJobs;
